import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
} from '@material-ui/core';
import RocketIcon from '@material-ui/icons/FlightTakeoff';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Header, Page, Content } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import { ReleaseDialog } from './ReleaseDialog';
import { PromotionWizard } from './PromotionWizard';

const ANNOTATION_PREFIX = 'salestrak.io/version-url';
const POLL_INTERVAL = 10_000;

const useStyles = makeStyles((theme) => ({
  table: {
    '& th': {
      fontWeight: 600,
    },
  },
  serviceName: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  serviceNameText: {
    fontWeight: 500,
  },
  deployingChip: {
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.6 },
  },
}));

interface ServiceEntry {
  name: string;
  versionUrls: Record<string, string>;
}

interface DeployRun {
  id: number;
  status: string;
  conclusion: string | null;
  service: string | null;
  environment: string | null;
  createdAt: string;
  url: string;
}

function VersionCell({ url }: { url: string | undefined }) {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);
  const [version, setVersion] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    let cancelled = false;

    discoveryApi
      .getBaseUrl('service-versions')
      .then((baseUrl) =>
        backstageFetch(
          `${baseUrl}/probe?url=${encodeURIComponent(url)}`,
        ),
      )
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const v = data.version ?? data.Version;
        if (typeof v !== 'string') throw new Error('complex response');
        setVersion(v);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url, discoveryApi, backstageFetch]);

  if (!url) return <span style={{ color: '#888' }}>&mdash;</span>;
  if (loading) return <span style={{ color: '#888' }}>...</span>;
  if (error) {
    return (
      <Tooltip title={`Failed to fetch ${url}/version`}>
        <Chip label="err" size="small" color="secondary" variant="outlined" />
      </Tooltip>
    );
  }
  return (
    <Chip
      label={version}
      size="small"
      variant="outlined"
      style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
    />
  );
}

function extractServices(entities: Entity[]): ServiceEntry[] {
  return entities
    .filter((e) => {
      const annotations = e.metadata.annotations ?? {};
      return ['dev', 'stage', 'prod'].some(
        (env) => annotations[`${ANNOTATION_PREFIX}-${env}`],
      );
    })
    .map((e) => {
      const annotations = e.metadata.annotations ?? {};
      const versionUrls: Record<string, string> = {};
      for (const env of ['dev', 'stage', 'prod']) {
        const url = annotations[`${ANNOTATION_PREFIX}-${env}`];
        if (url) versionUrls[env] = url;
      }
      return { name: e.metadata.name, versionUrls };
    });
}

function getActiveRun(runs: DeployRun[], serviceName: string): DeployRun | null {
  return (
    runs.find(
      (r) =>
        r.service === serviceName &&
        (r.status === 'queued' || r.status === 'in_progress'),
    ) ?? null
  );
}

function getLastRun(runs: DeployRun[], serviceName: string): DeployRun | null {
  return (
    runs.find(
      (r) => r.service === serviceName && r.status === 'completed',
    ) ?? null
  );
}

function DeployStatusChip({
  runs,
  serviceName,
}: {
  runs: DeployRun[];
  serviceName: string;
}) {
  const classes = useStyles();
  const active = getActiveRun(runs, serviceName);
  const last = getLastRun(runs, serviceName);

  if (active) {
    const label = active.status === 'queued'
      ? `queued → ${active.environment}`
      : `deploying → ${active.environment}`;
    return (
      <Tooltip title={`Click to view run`}>
        <Chip
          label={label}
          size="small"
          color="primary"
          className={classes.deployingChip}
          clickable
          component="a"
          href={active.url}
          target="_blank"
        />
      </Tooltip>
    );
  }

  if (last) {
    const isFailure = last.conclusion === 'failure';
    const label = isFailure
      ? `failed → ${last.environment}`
      : `deployed → ${last.environment}`;
    // Only show recent failures (last 10 minutes)
    if (isFailure) {
      const age = Date.now() - new Date(last.createdAt).getTime();
      if (age > 10 * 60 * 1000) return null;
    } else {
      return null; // Don't show stale success chips
    }
    return (
      <Tooltip title="Click to view run">
        <Chip
          label={label}
          size="small"
          color="secondary"
          variant="outlined"
          clickable
          component="a"
          href={last.url}
          target="_blank"
        />
      </Tooltip>
    );
  }

  return null;
}

export const ReleasePage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);

  const [services, setServices] = useState<ServiceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogService, setDialogService] = useState<ServiceEntry | null>(null);
  const [deployRuns, setDeployRuns] = useState<DeployRun[]>([]);
  const [mode, setMode] = useState<'list' | 'wizard'>('list');
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  const fetchDeployRuns = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('release-manager');
      const res = await backstageFetch(`${baseUrl}/deploy-runs`);
      if (res.ok) {
        const data = await res.json();
        setDeployRuns(data.runs ?? []);
      }
    } catch {
      // silent — polling will retry
    }
  }, [discoveryApi, backstageFetch]);

  useEffect(() => {
    catalogApi
      .getEntities({ filter: { kind: 'Component' } })
      .then(({ items }) => {
        setServices(extractServices(items));
      })
      .finally(() => setLoading(false));
  }, [catalogApi]);

  useEffect(() => {
    fetchDeployRuns();
    pollRef.current = setInterval(fetchDeployRuns, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchDeployRuns]);

  return (
    <Page themeId="tool">
      <Header
        title="Release"
        subtitle="Deploy services across environments"
      >
        {mode === 'list' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<RocketIcon />}
            onClick={() => setMode('wizard')}
          >
            Promote set of services
          </Button>
        )}
      </Header>
      <Content>
        {mode === 'wizard' ? (
          <PromotionWizard onExit={() => setMode('list')} />
        ) : loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : services.length === 0 ? (
          <Typography color="textSecondary">
            No services with version annotations found.
          </Typography>
        ) : (
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell>Dev</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Prod</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((svc) => (
                <TableRow key={svc.name}>
                  <TableCell>
                    <div className={classes.serviceName}>
                      <Typography variant="body2" className={classes.serviceNameText}>
                        {svc.name}
                      </Typography>
                      <DeployStatusChip runs={deployRuns} serviceName={svc.name} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <VersionCell url={svc.versionUrls.dev} />
                  </TableCell>
                  <TableCell>
                    <VersionCell url={svc.versionUrls.stage} />
                  </TableCell>
                  <TableCell>
                    <VersionCell url={svc.versionUrls.prod} />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => setDialogService(svc)}
                    >
                      Start Release
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <ReleaseDialog
          open={dialogService !== null}
          onClose={() => setDialogService(null)}
          service={dialogService}
        />
      </Content>
    </Page>
  );
};
